import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Toro } from '../common/enumerations';
import { ApiEndpointInfo } from '../models/api-endpoint-info.model';

@Injectable()
export class ApiUrlService {

    constructor() { }

    defaultEndpointIndex = environment.defaultApiEndpoint;

    apiEndpoints: ApiEndpointInfo[] = [
        new ApiEndpointInfo(Toro.Environment.Prod, 'https://smrtscape.com'),
        new ApiEndpointInfo(Toro.Environment.Beta, 'https://beta.smrtscape.com'),
        new ApiEndpointInfo(Toro.Environment.Dev, 'https://torosmrtscapeapidev.azurewebsites.net'),
        new ApiEndpointInfo(Toro.Environment.ITC, 'https://torosmrtscapeapiitc.azurewebsites.net'),
        new ApiEndpointInfo(Toro.Environment.LXC, 'https://torosmrtscapeapilxc.azurewebsites.net'),
        // new ApiEndpointInfo(Toro.Environment.Wifi, 'https://torosmrtscapeapiwifi.azurewebsites.net'),
        //For Local api server -  uncomment below api url for itc
        // new ApiEndpointInfo(Toro.Environment.LXC, 'http://localhost:13944'),
        new ApiEndpointInfo(Toro.Environment.Local, 'http://torosmrtscape.test'),];

    getApiEndpointUrl(env: Toro.Environment) {
        try {
            return this.apiEndpoints.find(e => e.environment === env).baseUrl;
        }
        catch (e) {
            throw Error('Unknown environment.');
        }
    }

    baseUrl = this.apiEndpoints[this.defaultEndpointIndex].baseUrl;

    private get baseApiUrl(): string { return this.baseUrl + '/api/v1/'; }
    get baseHealthCheckApiUrl(): string { return `${this.baseApiUrl}healthcheck`; }
    get baseUsersApiUrl(): string { return `${this.baseApiUrl}users`; }
    get baseRegisterApiUrl(): string { return `${this.baseApiUrl}register`; }
    get baseAccountsApiUrl(): string { return `${this.baseApiUrl}accounts`; }
    get baseIrrigationApiUrl(): string { return `${this.baseApiUrl}irrigation`; }
    get baseDevicesApiUrl(): string { return `${this.baseApiUrl}devices`; }
    get baseLocationsApiUrl(): string { return `${this.baseApiUrl}locations`; }
    get baseScenesApiUrl(): string { return `${this.baseApiUrl}scenes`; }
    get baseThemesApiUrl(): string { return `${this.baseApiUrl}themes`; }
    get baseColorsApiUrl(): string { return `${this.baseApiUrl}colors`; }
    get baseImageAssetsApiUrl(): string { return `${this.baseApiUrl}imageassets`; }
    get baseGatewaysApiUrl(): string { return `${this.baseApiUrl}gateways`; }
    get baseGatewayApiUrl(): string { return `${this.baseApiUrl}gateway`; }
    get baseMailCenterApiUrl(): string { return `${this.baseApiUrl}mailcenter`; }
    get baseDependentDevicesApiUrl(): string { return `${this.baseApiUrl}dependentdevices`; }
    get baseNotificationUrl(): string { return `${this.baseApiUrl}`; }
    get baseAdminApiUrl(): string { return `${this.baseApiUrl}admin`; }

    // =========================================================================================================================================================
    // Authorization
    // =========================================================================================================================================================
    getForgotPasswordUrl(email: string): string { return `${this.baseApiUrl}forgotpassword?email=${email}&useHostUrl=true`; }

    getValidateResetLinkUrl(id: string, secret: string): string { return `${this.baseApiUrl}login/validateResetLink?id=${id}&secret=${secret}`; }

    getResetPasswordUrl(password: string, id: string, secret: string) {
        return `${this.baseApiUrl}login/reset?newPassword=${password}&requestId=${id}&requestSecret=${secret}`;
    }

    getSendConfirmationEmailUrl(email: string): string { return `${this.baseApiUrl}sendconfirmationemail?email=${email}&useHostUrl=true`; }

    // =========================================================================================================================================================
    // Registration
    // =========================================================================================================================================================
    getValidateUserUrl(id: string, secret: string): string { return `${this.baseRegisterApiUrl}/confirm?id=${id}&secret=${secret}`; }

    get registerUserUrl(): string { return `${this.baseRegisterApiUrl}?useHostUrl=true`; }


    // =========================================================================================================================================================
    // Users
    // =========================================================================================================================================================
    get loginUrl(): string { return `${this.baseUsersApiUrl}?detailed=true`; }

    get userDetailsUrl(): string { return `${this.baseUsersApiUrl}?detailed=true`; }

    get changeUserEmail(): string { return `${this.baseUsersApiUrl}/changeUserEmail?useHostUrl=true`; }

    getUpdateUserInfoUrl(): string { return `${this.baseUsersApiUrl}/changeUserInfo`; }

    getUpdateUserPasswordUrl(): string { return `${this.baseUsersApiUrl}/changeUserPassword`; }

    getResetEmailUrl(userId: number, email: string, secret: string): string {
        return `${this.baseUsersApiUrl}/resetEmail?id=${userId}&newEmail=${email}&secret=${secret}`;
    }

    getNotificationPreferencesUrl(userId: number): string {
        return `${this.baseUsersApiUrl}/notificationPreference?userId=${userId}`;
    }

    setNotificationPreferencesUrl(userId: number): string {
        return `${this.baseUsersApiUrl}/notificationPreference?userId=${userId}&includeDiagnostics=false`;
    }
    // =========================================================================================================================================================
    // Accounts
    // =========================================================================================================================================================
    getAccountUrl(accountId: number): string { return `${this.baseAccountsApiUrl}/${accountId}`; }

    getAccountDetailsUrl(accountId: number): string { return `${this.getAccountUrl(accountId)}?detailed=true`; }

    getUpdateAccountAddressUrl(accountId: number): string { return `${this.baseAccountsApiUrl}/update/address/${accountId}`; }

    getDeleteAccountUrl(accountId: number): string { return `${this.baseAccountsApiUrl}/${accountId}/delete`; }

    getAddExistingUserAsRepresentativeUrl(accountId: number): string { return `${this.baseAccountsApiUrl}/${accountId}/addExistingUserAsRepresentative`; }

    getAddNewUserAsRepresentativeUrl(accountId: number): string { return `${this.baseAccountsApiUrl}/${accountId}/addNewUserAsRepresentative?useHostUrl=true`; }

    getRepresentatives(accountId: number, organizationId: number): string {
        return `${this.baseAccountsApiUrl}/${accountId}/representatives?organizationId=${organizationId}`;
    }

    getRemoveRepresentativeUrl(accountId: number, organizationId: number): string {
        return `${this.baseAccountsApiUrl}/${accountId}/removeRepresentative?organizationId=${organizationId}`;
    }

    getRemoveSelfAsRepresentativeUrl(organizationId: number): string {
        return `${this.baseAccountsApiUrl}/${organizationId}/removeSelfAsRepresentative`;
    }

    getNotificationUrl(accountId: number): string { return `${this.baseAccountsApiUrl}/${accountId}/notifications`; }

    getFirmwareInfoUrl(accountId: number): string { return `${this.baseAccountsApiUrl}/firmware/${accountId}`; }

    getFirmwareUpdateInfoUrl(accountId: number): string { return `${this.baseAccountsApiUrl}/getFirmwareUpdateInfo/${accountId}`; }


    // =========================================================================================================================================================
    // Admin
    // =========================================================================================================================================================
    getAdminInformationUrl(): string { return `${this.baseAdminApiUrl}/info`; }
    getDeviceTypeListUrl(): string { return `${this.baseAdminApiUrl}/devicetypes`; }
    getDeviceFirmwareUrl(id: number): string { return `${this.baseAdminApiUrl}/devicefirmware/${id}`; }
    getDeleteFirmwareUrl(id: number): string { return `${this.baseAdminApiUrl}/devicefirmware/${id}/delete`; }
    getUploadDeviceFirmwareUrl(id: number): string { return `${this.baseApiUrl}/admin/firmware/upload/${id}`; }
    getAdminMessagesUrl(): string { return `${this.baseAdminApiUrl}/messages`; }
    getReadMessageUrl(): string { return `${this.getAdminMessagesUrl()}/read`; }
    getMarkAsReadMessageUrl(id: string): string { return `${this.getAdminMessagesUrl()}/markread?id=${id}`; }
    getBuildMessageUrl(deviceUId: string, commandCode: number): string { return `${this.getAdminMessagesUrl()}/build?deviceUId=${deviceUId}&commandCode=${commandCode}`; }
    getFwBuildMessageUrl(deviceUId: string, commandCode: number, param1: string): string { return `${this.getAdminMessagesUrl()}/build?deviceUId=${deviceUId}&commandCode=${commandCode}&param1=${param1}`; }
    getSendMessageUrl(): string { return `${this.getAdminMessagesUrl()}/send`; }
    getAllUsersUrl(queryString: string): string { return `${this.baseAdminApiUrl}/usersearch${queryString}`; }
    getUserDetailsUrl(id: number): string { return `${this.baseAdminApiUrl}/userdetails?id=${id}`; }
    getAllAccountsUrl(queryString: string): string { return `${this.baseAdminApiUrl}/accountsearch${queryString}`; }
    getAllDevicesUrl(queryString: string): string { return `${this.baseAdminApiUrl}/device/search${queryString}`; }
    getCommcardUrl(commCardId: number): string {  return `${this.baseIrrigationApiUrl}/controllers/commCard/${commCardId}`; }
    getExportDevicesUrl(queryString: string): string { return `${this.baseAdminApiUrl}/device/export${queryString}`; }
    getReportUrl(gatewayId:string, commandId: number): string { return `${this.baseAdminApiUrl}/gateway/report?id=${gatewayId}&commandId=${commandId}`; }

    // =========================================================================================================================================================
    // Devices
    // =========================================================================================================================================================
    getUpgradeFirmwareUrl(accountId: number, deviceIdsQueryString: string): string {
        return `${this.baseDevicesApiUrl}/${accountId}/upgradeFirmware${deviceIdsQueryString}`;
    }

    getUpgradeDevicesFirmwareUrl(accountId: number): string {
        return `${this.baseDevicesApiUrl}/${accountId}/upgradeDevicesFirmware`;
    }

    getCancelFirmwareUpgradeUrl(deviceId: number, deviceTypeId: number): string {
        return `${this.baseDevicesApiUrl}/${deviceId}/cancelFirmwareUpgrade?deviceTypeId=${deviceTypeId}`;
    }


    // =========================================================================================================================================================
    // Location
    // =========================================================================================================================================================
    getLocationBaseUrl(locationId: number): string { return `${this.baseLocationsApiUrl}/${locationId}`; }

    getLocationDetailsUrl(locationId: number): string { return `${this.getLocationBaseUrl(locationId)}?detailed=true&includeSceneScheduleSummary=true`; }

    getGatewayStatusUrl(locationId: number): string { return `${this.getLocationBaseUrl(locationId)}?detailed=true&includeSceneScheduleSummary=false`; }

    getCreateLocationUrl(accountId: number): string { return `${this.baseLocationsApiUrl}/create/${accountId}`; }

    getUpdateLocationUrl(locationId: number): string { return `${this.baseLocationsApiUrl}/edit/${locationId}`; }

    getDeleteLocationUrl(locationId: number): string { return `${this.baseLocationsApiUrl}/delete/${locationId}`; }

    getControllersForLocationUrl(locationId: number): string { return `${this.getLocationBaseUrl(locationId)}/controllerDetails`; }

    getControllersListForLocationUrl(locationId: number, gatewayId: number): string { return `${this.getLocationBaseUrl(locationId)}/controllerlist?gatewayId=${gatewayId}`; }

    getStopIrrigationForLocationUrl(locationId: number): string { return `${this.getLocationBaseUrl(locationId)}/irrigation/stop`; }

    getWeatherForecastUrl(latitude: number, longitude: number): string {
        return `${this.baseLocationsApiUrl}/getWeatherForecast?latitude=${latitude}&longitude=${longitude}`;
    }

    getActiveControllers(locationId: number): string { return `${this.getLocationBaseUrl(locationId)}/activeControllers`; }

    getControllerDetailsUrl(locationId: number, controllerId: number): string {
        return `${this.baseLocationsApiUrl}/${locationId}/singleControllerDetails/${controllerId}`;
    }

    getAddControllerUrl(locationId: number) { return `${this.baseLocationsApiUrl}/${locationId}/controller/add`; }

    // =========================================================================================================================================================
    // Scene
    // =========================================================================================================================================================

    getAllScenesByLocation(locationId: number): string { return `${this.baseScenesApiUrl}/byLocation/${locationId}`; }

    getSceneStatusUrl(locationId: number): string { return `${this.baseScenesApiUrl}/status/byLocation/${locationId}`; }

    getSceneSchedulesUrl(sceneId: number): string { return `${this.baseScenesApiUrl}/${sceneId}/schedules`; }

    getThemesByScene(sceneId: number): string { return `${this.baseScenesApiUrl}/${sceneId}/themes`; }

    getForceSceneOnUrl(sceneId: number, duration: number): string { return `${this.baseScenesApiUrl}/${sceneId}/force?action=on&duration=${duration}`; }

    getForceSceneOffUrl(sceneId: number): string { return `${this.baseScenesApiUrl}/${sceneId}/force?action=off&duration=0`; }

    getForceAllScenesOnUrl(locationId: number, duration: number): string {
        return `${this.baseScenesApiUrl}/forceByLocation/${locationId}?action=on&duration=${duration}`;
    }

    getForceAllScenesOffUrl(locationId: number): string {
        return `${this.baseScenesApiUrl}/forceByLocation/${locationId}?action=off&duration=0`;
    }

    getCreateSceneUrl(locationId: number): string {
        return `${this.baseScenesApiUrl}/${locationId}/create`;
    }

    getUpdateSceneUrl(sceneId: number, updateScene: boolean, updateSchedules: boolean): string {
        return `${this.baseScenesApiUrl}/${sceneId}/update?updateScene=${updateScene}&updateSchedules=${updateSchedules}`;
    }

    getDeleteSceneUrl(sceneId: number): string {
        return `${this.baseScenesApiUrl}/${sceneId}/delete`;
    }

    // =========================================================================================================================================================
    // Theme
    // =========================================================================================================================================================
    getThemeStatusUrl(locationId: number): string { return `${this.baseThemesApiUrl}/status/byLocation/${locationId}`; }

    getThemeSchedulesUrl(themeId: number): string { return `${this.baseThemesApiUrl}/${themeId}/schedules`; }

    getForceThemeOnUrl(themeId: number, duration: number): string { return `${this.baseThemesApiUrl}/${themeId}/force?action=on&duration=${duration}`; }

    getForceThemeOffUrl(themeId: number): string { return `${this.baseThemesApiUrl}/${themeId}/force?action=off&duration=0`; }

    getForceAllThemesOnUrl(locationId: number): string {
        return `${this.baseThemesApiUrl}/${locationId}/resume`;
    }

    getForceAllThemesOffUrl(locationId: number): string {
        return `${this.baseThemesApiUrl}/${locationId}/resume`;
    }

    getCreateThemeUrl(locationId: number): string {
        return `${this.baseThemesApiUrl}/${locationId}/create`;
    }

    getUpdateThemeUrl(themeId: number): string {
        return `${this.baseThemesApiUrl}/${themeId}/update`;
    }

    getDeleteThemeUrl(themeId: number): string {
        return `${this.baseThemesApiUrl}/${themeId}/delete`;
    }

    getThemeDetailsUrl(themeId: number): string {
        return `${this.baseThemesApiUrl}/${themeId}`
    }

    getThemesUrl(locationId: number): string {
        return `${this.baseThemesApiUrl}/byLocation/${locationId}`
    }

    setDefaultThemeForScenesUrl(locationId: number): string {
        return `${this.baseLocationsApiUrl}/${locationId}/setdefaulttheme`;
    }
    // =========================================================================================================================================================
    // Colors
    // =========================================================================================================================================================
    getAddColorUrl(accountId: number): string {
        return `${this.baseColorsApiUrl}/${accountId}/add`;
    }

    getGetColorsUrl(accountId: number, type: number): string {
        return `${this.baseColorsApiUrl}/${accountId}?type=${type}`;
    }

    getDeleteColorUrl(id: number, type: number): string {
        return `${this.baseColorsApiUrl}/delete/${id}/${type}`;
    }

    // =========================================================================================================================================================
    // Image Asset
    // =========================================================================================================================================================
    getImageAssetUrl(imageAssetId: number): string { return `${this.baseImageAssetsApiUrl}/getImage/${imageAssetId}`; }

    getImageAssetWithResolutionUrl(imageAssetId: number, width: number, height: number): string {
        return `${this.baseImageAssetsApiUrl}/getImage/${imageAssetId}/${width}/${height}?crop=true`;
    }

    getUploadImageUrl(): string { return `${this.baseImageAssetsApiUrl}/uploadImage`; }


    // =========================================================================================================================================================
    // Gateways
    // =========================================================================================================================================================
    getCreateGatewayUrl(locationId: number) { return `${this.baseGatewaysApiUrl}/create/${locationId}`; }

    getUpdateGatewayUrl(locationId: number, gatewayId: number) { return `${this.baseGatewaysApiUrl}/update/${locationId}/${gatewayId}`; }

    getDeleteGatewayUrl(gatewayId: number) { return `${this.baseGatewaysApiUrl}/delete/${gatewayId}`; }

    getResyncGatewayUrl(gatewayId: number) { return `${this.baseGatewaysApiUrl}/resync/${gatewayId}`; }

    getPutGatewayInDiscoveryModeUrl(gatewayId: number, accountId: number, locationId: number) {
        return `${this.baseGatewaysApiUrl}/placeindiscoverymode/${gatewayId}?accountId=${accountId}&locationId=${locationId}`;
    }

    getCancelGatewayDiscoveryModeUrl(gatewayId: number) {
        return `${this.baseGatewaysApiUrl}/cancelGatewayDiscoveryMode/${gatewayId}`;
    }

    getDiscoverGatewayUrl(replace: boolean, accountId: number) {
        return `${this.baseGatewaysApiUrl}/discovergateway?replace=${replace}&accountId=${accountId}`;
    }

    getGatewayNetworkConfigurationUrl(gatewayId: number, refresh: boolean): string {
        return `${this.baseGatewaysApiUrl}/${gatewayId}/networkConfiguration?refresh=${refresh}`;
    }

    getUpdateGatewayNetworkConfigurationUrl(gatewayId: number): string {
        return `${this.baseGatewaysApiUrl}/${gatewayId}/updateNetworkConfiguration`;
    }

    getGatewayStatusUrl1(gatewayId: number): string {
        return `${this.baseGatewaysApiUrl}/${gatewayId}`;
    }

    // PS: Gateway
    getWakeupUrl(gatewayId: number): string { return `${this.baseGatewayApiUrl}/commands/awake/${gatewayId}`; }

    // =========================================================================================================================================================
    // Irrigation
    // =========================================================================================================================================================
    getControllerTypesUrl(): string { return `${this.baseIrrigationApiUrl}/controllers/types`; }

    getEditControllerUrl(controllerId: number) {
        return `${this.baseIrrigationApiUrl}/controllers/editController/${controllerId}`;
    }

    getDeleteControllerUrl(controllerId: number): string {
        return `${this.baseIrrigationApiUrl}/controllers/delete/${controllerId}`;
    }

    getSaveProgramUrl(controllerId: number, programNumber: number): string {
        return `${this.baseIrrigationApiUrl}/${controllerId}/programs/edit/${programNumber}`;
    }

    getSaveScheduleUrl(controllerId: number, programNumber: number): string {
        return `${this.baseIrrigationApiUrl}/${controllerId}/schedules/edit/${programNumber}`;
    }

    getStopAllIrrigationforControllerUrl(controllerId: number): string { return `${this.baseIrrigationApiUrl}/controllers/${controllerId}/stations/stop`; }

    getStopStationIrrigationUrl(controllerId: number,stationNumber:number): string { return `${this.baseIrrigationApiUrl}/controllers/${controllerId}/stations/${stationNumber}/stop`; }

    getStopProgramIrrigationUrl(controllerId: number,programNumber:number): string { return `${this.baseIrrigationApiUrl}/controllers/${controllerId}/programs/${programNumber}/stop`; }

    getControllerStatusUrl(controllerId: number): string { return `${this.baseIrrigationApiUrl}/controllers/${controllerId}/controllerstatus`; }

    getControllerRunningStatusUrl(controllerId: number): string { return `${this.baseIrrigationApiUrl}/controllers/${controllerId}/runningstatus`; }

    getStartStationUrl(controllerId: number, stationNumber: number, duration: number): string {
        return `${this.baseIrrigationApiUrl}/controllers/${controllerId}/stations/${stationNumber}/start?duration=${duration}`;
    }

    getStartProgramUrl(controllerId: number, programNumber: number): string {
        return `${this.baseIrrigationApiUrl}/controllers/${controllerId}/programs/${programNumber}/start`;
    }

    getUpdateStationUrl(controllerId: number, stationNumber: number): string {
        return `${this.baseIrrigationApiUrl}/stations/edit/${controllerId}/${stationNumber}`;
    }

    getTestAllStationsUrl(controllerId: number, durationInMinutes: number, maxStations: number, includeDiagnostics: boolean): string {
        return `${this.baseIrrigationApiUrl}/controllers/${controllerId}/stations/test?duration=${durationInMinutes}&maxStations=${maxStations}&includeDiagnostics=${includeDiagnostics}`;
    }

    getStartAuxiliaryUrl(controllerId: number, auxiliaryNumber: number): string {
        return `${this.baseIrrigationApiUrl}/controllers/${controllerId}/auxiliaries/${auxiliaryNumber}/start`;
    }

    getUpdateAuxiliaryUrl(controllerId: number, auxiliaryNumber: number): string {
        return `${this.baseIrrigationApiUrl}/auxiliaries/edit/${controllerId}/${auxiliaryNumber}`;
    }

    getReplaceCommCardUrl(controllerId: number, deviceIdSeenOnScreen: string, controllerTypeId: number, pin?: number): string {
        const pinParam = pin ? `?pin=${pin}` : '';

        return `${this.baseIrrigationApiUrl}/controller/${controllerId}/replaceCommCard/${deviceIdSeenOnScreen}/${controllerTypeId}${pinParam}`;
    }

    getSendRecordsToControllerUrl(controllerId: number): string {
        return `${this.baseIrrigationApiUrl}/controller/${controllerId}/sendRecordsToController`;
    }

    getControllerRecordsUrl(controllerId: number, forceGetFromController = false): string {
        return `${this.baseIrrigationApiUrl}/controller/${controllerId}/getControllerRecords${forceGetFromController ? '?forceGetFromController=true' : ''}`;
    }

    getControllerConfigDisplayInfoUrl(controllerId: number) {
        return `${this.baseIrrigationApiUrl}/controller/${controllerId}/configDisplayInfo`;
    }

    getResyncCommunicationCardUrl(controllerId: number): string {
        return `${this.baseIrrigationApiUrl}/controller/${controllerId}/resyncCommunicationCard`;
    }

    getRunningModeUrl(controllerId: number, runningMode: number, daysToRemainOff: number) {
        return `${this.baseIrrigationApiUrl}/controllers/${controllerId}/running-mode/${runningMode}?resumeInDays=${daysToRemainOff}`;
    }

    getExportConfigurationUrl(controllerId: number): string { return `${this.baseIrrigationApiUrl}/export/${controllerId}`; }

    getImportConfigurationUrl(controllerId: number): string { return `${this.baseIrrigationApiUrl}/import/${controllerId}`; }

    getControllerProgrammabilityUxUrl(controllerId: number): string { return `${this.baseIrrigationApiUrl}/controllers/${controllerId}/programmability`; }

    // =========================================================================================================================================================
    // Mail Center
    // =========================================================================================================================================================
    getRecentMessagesUrl(accountId: number, locationId: number, lastMessageId: string = null): string {
        return `${this.baseMailCenterApiUrl}/${accountId}/getRecentMessages/${locationId}${lastMessageId ? '?lastMessageId=' + lastMessageId : ''}`;
    }

    getMarkMessagesAsReadUrl(accountId: number, messageIdsString: string): string {
        return `${this.baseMailCenterApiUrl}/${accountId}/markMessagesAsRead?${messageIdsString}`;
    }

    getDeleteMessages(accountId: number, messageIdsString: string): string {
        return `${this.baseMailCenterApiUrl}/${accountId}/deleteMessages?${messageIdsString}`;
    }


    // =========================================================================================================================================================
    // Dependent Devices
    // =========================================================================================================================================================
    getDependentDeviceActivateDiscoveryModeUrl(gatewayId: number, deviceType: Toro.DeviceType): string {
        return `${this.baseDependentDevicesApiUrl}/${gatewayId}/activateDiscoveryMode?deviceType=${deviceType}`;
    }

    getDependentDeviceCancelDiscoveryModeUrl(gatewayId: number): string { return `${this.baseDependentDevicesApiUrl}/${gatewayId}/cancelDiscoveryMode`; }

    getDevicesUrl(gatewayId: number, withoutPagination: boolean = false, pageNumber: number = 0, pageSize: number): string {
        return `${this.baseDependentDevicesApiUrl}/${gatewayId}/getLpcus?withoutPagination=${withoutPagination}&pageNumber=${pageNumber}&pageSize=${pageSize}
    `;
    }

    getDeviceUrl(lpcuId: number): string { return `${this.baseDependentDevicesApiUrl}/device/${lpcuId}`; }

    getFixtureDetailsUrl(deviceId: number, theme: { themeOrdinalPosition: number, themeId: number }): string { return `${this.baseDependentDevicesApiUrl}/fixture/${deviceId}?themeOrdinalPostion=${theme.themeOrdinalPosition}&&themeId=${theme.themeId}`; }

    getScanDeviceUrl(lpcuId: number, isMagneto: boolean): string { return `${this.baseDependentDevicesApiUrl}/device/${lpcuId}/scan?isMagneto=${isMagneto}`; }

    getPreviewDeviceUrl(lpcuId: number): string { return `${this.baseDependentDevicesApiUrl}/preview/${lpcuId}`; }

    getAddLpcuUrl(gatewayId: number): string { return `${this.baseDependentDevicesApiUrl}/${gatewayId}/addLpcu`; }

    getUpdateLpcuUrl(deviceId: number): string { return `${this.baseDependentDevicesApiUrl}/${deviceId}/updateLpcu`; }

    getAddMagnetoUrl(gatewayId: number): string { return `${this.baseDependentDevicesApiUrl}/${gatewayId}/addMagneto`; }

    getUpdateMagnetoUrl(deviceId: number): string { return `${this.baseDependentDevicesApiUrl}/${deviceId}/updateMagneto`; }

    getDeleteDependentDeviceUrl(deviceId: number): string { return `${this.baseDependentDevicesApiUrl}/${deviceId}/delete`; }


    // =========================================================================================================================================================
    // Google geo-code api
    // =========================================================================================================================================================
    getGoogleGeocodeUrl(address: string) {
        return 'https://maps.googleapis.com/maps/api/geocode/json?key=AIzaSyCjd8hXEteOu96WVdw9n4MX4sJIn3TdRtw&address=' + address;
        // return 'https://maps.googleapis.com/maps/api/geocode/json?key=AIzaSyD_vzhQUzdfSZhhhk8iXQ34wofO8F-FJys&address=' + address;
    }


    getServerAPIVersionUrl() {
        return `${this.baseApiUrl}smartscapeversion`;
    }
}
